import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Home,
  Search,
  Heart,
  Bell,
  Settings,
  Sun,
  Moon,
  Grid,
  Users
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';
import { formatLastSeen } from '../../utils/activityHelpers';
import { useFriends } from '../../context/FriendsContext';
import { useNotifications } from '../../context/NotificationContext';
import { useShelf } from '../../context/ShelfContext';
import { useTheme } from '../../context/ThemeContext';

const SidebarContainer = styled.aside`
  width: 280px;
  background: ${({ theme }) => theme.isDarkMode ? '#181A1B' : '#EFF3F7'};
  height: 100vh;
  position: fixed;
  left: var(--vk-offset, 0px);
  top: var(--polka-safe-top, 0px);
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
  transition: background-color 0.3s ease, border-color 0.3s ease;

  @media (max-width: 1024px) {
    width: 80px;
    padding: 20px 10px;
  }

  z-index: 101;

  @media (max-width: 768px) {
    transform: translateX(-100%);
    z-index: 1000;
    width: 280px;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    
    &.open {
      transform: translateX(0);
    }
  }
`;

const LogoSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 60px;
  padding: 0 24px;
`;

const LogoImage = styled.img`
  height: 22px;
  width: auto;
  filter: ${({ theme }) => theme.isDarkMode
    ? 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
    : 'brightness(0) saturate(100%) invert(9%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'};
  transition: filter 0.3s ease;
`;

const ProfileSection = styled.div`
  padding: 0 24px;
  margin-bottom: 36px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const AvatarLink = styled(Link)`
  text-decoration: none;
  cursor: pointer;
  display: inline-block;
  flex-shrink: 0;
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'};
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const UserName = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Username = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
`;

const Bio = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
  line-height: 1.4;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
  width: 100%;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  margin-top: 2px;
`;

const MenuSection = styled.nav`
  padding: 0 16px;
  margin-bottom: 36px;
`;

const FandomIcon = styled.img`
  width: 22px;
  height: 22px;
  filter: ${({ theme }) => theme.isDarkMode
    ? 'brightness(0) saturate(100%) invert(55%)'
    : 'brightness(0) saturate(100%) invert(40%)'};
  transition: filter 0.3s ease;
`;

const ShelfIcon = styled.img`
  width: 22px;
  height: 22px;
  filter: ${({ theme }) => theme.isDarkMode
    ? 'brightness(0) saturate(100%) invert(55%)'
    : 'brightness(0) saturate(100%) invert(40%)'};
  transition: filter 0.3s ease;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;
  border-radius: 10px;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
  }

  &.active {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};

    svg {
      filter: drop-shadow(0 0 4px ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.3)'});
    }

    img {
      filter: ${({ theme }) => theme.isDarkMode
        ? 'brightness(0) saturate(100%) invert(100%)'
        : 'brightness(0) saturate(100%) invert(9%)'};
    }
  }

  @media (max-width: 1024px) {
    padding: 12px;
    justify-content: center;
    
    span {
      display: none;
    }
  }
`;

const NotificationBadge = styled.span`
  background: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
  color: ${({ theme }) => theme.isDarkMode ? '#181A1B' : '#ffffff'};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const FriendsSection = styled.div`
  padding: 0 24px;
  flex: 1;
`;

const FriendsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const FriendsTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
`;

const ViewAllLink = styled(Link)`
  font-size: 12px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
  text-decoration: none;
`;

const FriendsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FriendsEmpty = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  padding: 8px 4px;
`;

const FriendItem = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const FriendAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const FriendInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FriendName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
`;

const FriendAction = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
`;

const BottomActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 12px 24px 0;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)'};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  }
`;

const Sidebar = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { friends, followers } = useFriends();
  const { notifications } = useNotifications();
  const { shelfItems } = useShelf();
  const { isDarkMode, toggleTheme } = useTheme();

  const unreadCount = notifications?.filter(n => !n.read).length || 0;
  const topFriends = friends?.slice(0, 4) || [];
  const shelfCount = Array.isArray(shelfItems) ? shelfItems.length : (user?.stats?.items || 0);
  const friendsCount = Array.isArray(friends) ? friends.length : (user?.stats?.friends || 0);
  const followersCount = (Array.isArray(followers) ? followers.length : 0) || user?.stats?.followers || 0;

  return (
    <SidebarContainer>
      {/* Logo */}
      <LogoSection>
        <LogoImage src="/assets/PolkaLogo.svg" alt={t('app.name')} />
      </LogoSection>

      {/* Profile */}
      {user && (
        <ProfileSection>
          <ProfileRow>
            <AvatarLink to="/profile">
              <Avatar src={user.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'} alt={user.name} />
            </AvatarLink>
            <ProfileInfo>
              <UserName>{user.name}</UserName>
              <Username>@{user.username}</Username>
            </ProfileInfo>
          </ProfileRow>
          {user.bio && <Bio>{user.bio}</Bio>}
          
          <StatsContainer>
            <StatItem>
              <StatValue>{shelfCount}</StatValue>
              <StatLabel>{t('sidebar.onShelf')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{friendsCount}</StatValue>
              <StatLabel>{t('sidebar.friends')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{followersCount}</StatValue>
              <StatLabel>{t('sidebar.followers')}</StatLabel>
            </StatItem>
          </StatsContainer>
        </ProfileSection>
      )}

      {/* Menu */}
      <MenuSection>
        <NavItem to="/">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Home size={22} />
            <span>{t('nav.feed')}</span>
          </div>
        </NavItem>

        <NavItem to="/search">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Search size={22} />
            <span>{t('nav.search')}</span>
          </div>
        </NavItem>

        <NavItem to="/shelf">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <ShelfIcon src="/polkaicon.svg" alt={t('nav.myShelf')} />
            <span>{t('nav.myShelf')}</span>
          </div>
        </NavItem>

        <NavItem to="/wishlist">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Heart size={22} />
            <span>{t('nav.wishlist')}</span>
          </div>
        </NavItem>

        <NavItem to="/collections">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Grid size={22} />
            <span>{t('nav.polkaCollections')}</span>
          </div>
        </NavItem>

        <NavItem to="/fandoms">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <FandomIcon src="/fandom.svg" alt={t('nav.fandoms')} />
            <span>{t('nav.fandoms')}</span>
          </div>
        </NavItem>

        <NavItem to="/notifications">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Bell size={22} />
            <span>{t('nav.notifications')}</span>
          </div>
          {unreadCount > 0 && <NotificationBadge>{unreadCount}</NotificationBadge>}
        </NavItem>
      </MenuSection>

      {/* Friends */}
      <FriendsSection>
        <FriendsHeader>
          <FriendsTitle>{t('sidebar.friendsTitle')}</FriendsTitle>
          <ViewAllLink to="/friends">{t('sidebar.viewAll')}</ViewAllLink>
        </FriendsHeader>

        {topFriends.length > 0 ? (
          <FriendsList>
            {topFriends.map(friend => (
              <FriendItem key={friend.id} to={`/profile/${friend.username}`}>
                <FriendAvatar 
                  src={friend.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'} 
                  alt={friend.name}
                />
                <FriendInfo>
                  <FriendName>{friend.name}</FriendName>
                  <FriendAction>
                    {(() => {
                      const status = formatLastSeen(friend.last_seen);
                      if (status === 'online') {
                        return t('sidebar.online');
                      }
                      if (status) {
                        return t('sidebar.wasOnlineTime', { time: status });
                      }
                      return t('sidebar.wasOnline');
                    })()}
                  </FriendAction>
                </FriendInfo>
              </FriendItem>
            ))}
          </FriendsList>
        ) : (
          <FriendsEmpty>{t('sidebar.noFriends')}</FriendsEmpty>
        )}
      </FriendsSection>

      {/* Bottom buttons */}
      <BottomActions>
        <IconButton as={NavLink} to="/settings" title={t('nav.settings')} style={{ textDecoration: 'none' }}>
          <Settings size={20} />
        </IconButton>
        <IconButton onClick={toggleTheme} title={isDarkMode ? t('nav.lightTheme') : t('nav.darkTheme')}>
          {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
        </IconButton>
      </BottomActions>
    </SidebarContainer>
  );
};

export default Sidebar;
